import type { InputHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { focusRing } from "./buttons";

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  id: string;
  label: string;
  error?: string | null;
  hint?: string;
}

/** Labelled input of the new design: 48 px, radius 12, input surface, explicit error state. */
export function TextField({ id, label, error, hint, className, ...props }: TextFieldProps) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-semibold text-g-ink-700">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          "h-12 w-full rounded-g-md border bg-g-bg-input px-3.5 text-[15px] text-g-ink-900 transition-colors duration-150 placeholder:text-g-ink-400",
          "hover:border-g-line-strong disabled:cursor-not-allowed disabled:text-g-ink-400",
          error ? "border-g-danger bg-g-danger-tint" : "border-g-line",
          focusRing,
          className,
        )}
        {...props}
      />
      {error ? (
        <p id={`${id}-error`} className="text-xs font-medium text-g-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-g-ink-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
